import { parseUtcIso } from "@/lib/date-utils";

/**
 * Whether a resource row has a real machine behind it at the provider.
 *
 * The admin half of `Resource.HasProviderVm()` (`WiCloud.Domain/Common/ResourceExtensions.cs`) and
 * of the client console's `hasProviderVm` in `wi-cloud-client/src/lib/resourceStatus.ts`. The three
 * must keep answering the same way: anything deciding "is there a machine behind this row" — the
 * delete guards, the renewal gates, the two consoles' delete dialogs — reads it through one of
 * these, or two screens disagree about one VM.
 *
 * Deliberately duplicated rather than shared: the two frontends are separate applications with no
 * common package, so a cross-app import is not available. Keep this file in step with the client's
 * copy by hand.
 */

/**
 * Prefix of a provider id minted in simulation mode, which stands for no real machine.
 * Mirrors `ResourceExtensions.SimulatedProviderIdPrefix` on the backend.
 */
const SIMULATED_PROVIDER_ID_PREFIX = "SIMULATED-";

/**
 * True when WiCloud holds a provider identifier naming a machine that actually exists.
 *
 * A `SIMULATED-` id counts as none: it is minted in simulation mode and exists on no hypervisor,
 * so every provider-backed feature already treats it as absent — 23 rows on dev carry one.
 */
export function hasProviderVm(providerResourceId?: string | null): boolean {
  const id = providerResourceId?.trim() ?? "";

  return id.length > 0 && !id.startsWith(SIMULATED_PROVIDER_ID_PREFIX);
}

/**
 * True when an ordinary deletion of this row leaves it in place as Terminating instead of removing
 * it: a VM with a real machine, deleted at the infrastructure. Mirrors the backend's `restorable`
 * branch in `AdminDeleteResourceCommandHandler` — the row stays visible (DeletedAt null) until n8n
 * confirms, then moves to the Pending purge view (filter value `Restorable`). Every other deletion is
 * finished when it answers.
 */
export function isRestorableDeletion(
  resourceType: string,
  providerResourceId: string | null | undefined,
  deleteFromProvider: boolean,
): boolean {
  return (
    deleteFromProvider &&
    resourceType === "VirtualMachine" &&
    hasProviderVm(providerResourceId)
  );
}

/**
 * Whether "Restore" applies to this row: a deleted VM (the API sends `restorableUntil` only for one)
 * still inside its window. Mirrors ResourceReinstallService's restore checks on the window; the paid
 * term and the name are the API's to refuse. The role check is the caller's.
 */
export function canRestoreDeletion(
  resource: {
    type: string;
    status: string;
    restorableUntil?: string | null;
  },
  now: number = Date.now(),
): boolean {
  if (resource.type !== "VirtualMachine") {
    return false;
  }

  if (resource.status !== "Decommissioning" || !resource.restorableUntil) {
    return false;
  }

  return parseUtcIso(resource.restorableUntil).getTime() > now;
}

/**
 * Whether "Finalize deletion now" applies to this row: a deleted VM (the API sends `restorableUntil`
 * only for one) whose kept window has closed. Mirrors FinalizeResourceDeletionCommandHandler's checks,
 * so the item is shown exactly when the request would be accepted; the role check is the caller's.
 */
export function canFinalizeDeletion(
  resource: {
    type: string;
    status: string;
    restorableUntil?: string | null;
  },
  now: number = Date.now(),
): boolean {
  if (resource.type !== "VirtualMachine") {
    return false;
  }

  if (resource.status !== "Decommissioning" || !resource.restorableUntil) {
    return false;
  }

  return parseUtcIso(resource.restorableUntil).getTime() <= now;
}
